import { Button } from '@thanx/uikit/button'
import { Notification } from '@thanx/uikit/notification'
import { Text } from '@thanx/uikit/text'
import type { PreparerAdmin } from 'api/recoveryAi.types'
import Input from 'components/Form/Input'
import ModalContainer from 'components/ModalContainer'
import Spinner from 'components/Spinner'
import palette from 'constants/palette'
import Form from 'formsy-react'
import { buildTranslate } from 'locales'
import React, { useState } from 'react'
import { useStyletron } from 'styletron-react'
import { getRelativeTime } from 'utilities/date'
import StatusPill from '../components/StatusPill'
import type { PreparerAdminsState } from './usePreparerAdmins'

const t = buildTranslate('recovery_ai.preparer')

// How many admins show before the "View N more" expander.
const INITIAL_VISIBLE_ADMINS = 3
// The section break above the first row is deliberately larger than the gap between
// rows — uniform padding collapses it and the list reads as one undifferentiated run.
const SECTION_BREAK = '52px'
const ROW_GAP = '21px'
// Fixes the right-hand column so a row doesn't reflow when a ~95px Notify button
// becomes a ~47px Resend text link.
const ROW_CONTROL_MIN_WIDTH = '150px'
const MESSAGE_ROWS = 4
// Parity with the other admin-composed free-text-to-email fields (SendMessageModal).
const MESSAGE_MAX_LENGTH = 2000

interface Props {
  isOpen: boolean
  onClose: () => void
  admins: PreparerAdmin[]
  adminsState: PreparerAdminsState
  merchantName: string | null
  // First name of the preparer, interpolated into the message's default draft.
  preparerFirstName: string | null
  activationUrl: string
  onNotify: (id: string, message: string) => void
  onCopyLink: () => void
  // The notify mutation is in flight — disables every Notify/Resend control so a
  // double-click can't fire a duplicate write (the mutation is one shared
  // instance across every row, not per-admin).
  isNotifying: boolean
  // The last notify/resend attempt failed (e.g. a stale admin id) — shown inline
  // rather than as a toast, since the modal stays open for a retry.
  notifyError: boolean
}

// AI-23 preparer (non-admin) handoff modal. A non-admin who configured RecoveryAI
// can't activate it themselves — activation commits the merchant to charges — so
// they hand off to an account admin.
//
// AI-923 reverses the earlier "derivable client-side" call: the message is an
// EDITABLE draft, seeded with a default greeting the preparer can rewrite before
// it is carried to Recovery::AdminActivationRequests::Notify and escaped
// server-side into the email body. The activation URL stays a separate,
// non-editable line — it must survive an edit that clears the textarea.
const PreparerHandoffModal = ({
  isOpen,
  onClose,
  admins,
  adminsState,
  merchantName,
  preparerFirstName,
  activationUrl,
  onNotify,
  onCopyLink,
  isNotifying,
  notifyError,
}: Props) => {
  const [css] = useStyletron()
  const [expanded, setExpanded] = useState(false)

  const defaultMessage = preparerFirstName
    ? t('preview_message', { name: preparerFirstName })
    : t('preview_message_fallback')
  // null = the preparer hasn't touched the field yet, so a preparerFirstName
  // that resolves after mount (currentUser hydrates async, see index.js) still
  // flows into the default greeting instead of freezing the pre-hydration
  // snapshot. components/Form/Input only treats a field as controlled when
  // `inputProps.value` is set (see its formControl()) — passing `value` here
  // as a plain prop makes Input fall back to an uncontrolled `defaultValue`.
  const [draft, setDraft] = useState<string | null>(null)
  const message = draft ?? defaultMessage
  // A cleared draft still displays as blank (the preparer's choice while
  // editing), but must not ship an empty email body — fall back silently.
  const messageToSend = message.trim() === '' ? defaultMessage : message

  // Reset the expander + message draft on close so neither survives into the
  // next open — ConfiguredSummary keeps this modal mounted (toggling isOpen).
  const handleClose = () => {
    setExpanded(false)
    setDraft(null)
    onClose()
  }

  const visibleAdmins = expanded
    ? admins
    : admins.slice(0, INITIAL_VISIBLE_ADMINS)
  const hiddenCount = admins.length - INITIAL_VISIBLE_ADMINS

  // react-redux-i18n does NOT auto-pluralize; pick _one/_other at the call site.
  const expanderLabel =
    hiddenCount === 1
      ? t('view_more_one', { count: hiddenCount })
      : t('view_more_other', { count: hiddenCount })

  const renderRowControl = (admin: PreparerAdmin) => {
    if (admin.notifiedAt) {
      return (
        <>
          <Text.BodyText5 color="grey70">
            {t('notified', { time: getRelativeTime(admin.notifiedAt) })}
          </Text.BodyText5>
          <Button
            kind="minimal"
            disabled={isNotifying}
            onClick={() => onNotify(admin.id, messageToSend)}
            aria-label={t('resend_aria', { name: admin.name })}
          >
            {t('resend')}
          </Button>
        </>
      )
    }
    return (
      <Button
        kind="secondary"
        size="small"
        disabled={isNotifying}
        onClick={() => onNotify(admin.id, messageToSend)}
        aria-label={t('notify_aria', { name: admin.name })}
      >
        <i className="fa fa-envelope-o mr-xs" aria-hidden="true" />
        {t('notify')}
      </Button>
    )
  }

  const renderAdmins = () => {
    if (adminsState === 'loading') {
      return (
        <div role="status" aria-label={t('title')}>
          <Spinner isLoading />
        </div>
      )
    }
    // Never a bare empty region — a blank block here reads as broken software.
    if (adminsState === 'unavailable') {
      return (
        <Text.BodyText4 tag="div" color="grey70">
          {t('admins_unavailable')}
        </Text.BodyText4>
      )
    }
    if (admins.length === 0) {
      return (
        <Text.BodyText4 tag="div" color="grey70">
          {t('admins_empty')}
        </Text.BodyText4>
      )
    }
    return (
      <>
        <div className={css({ marginTop: ROW_GAP })}>
          {visibleAdmins.map(admin => (
            <div
              key={admin.id}
              className={`d-flex justify-content-between align-items-center ${css(
                {
                  padding: `${ROW_GAP} 0`,
                  borderBottom: `1px solid ${palette.grey20}`,
                }
              )}`}
            >
              <div className="d-flex flex-column">
                <Text.BodyText4 bold>{admin.name}</Text.BodyText4>
                <Text.BodyText5 color="grey70">{admin.email}</Text.BodyText5>
              </div>
              <div
                className={`d-flex align-items-center justify-content-end ${css(
                  { gap: '10px', minWidth: ROW_CONTROL_MIN_WIDTH }
                )}`}
              >
                {renderRowControl(admin)}
              </div>
            </div>
          ))}
        </div>

        {hiddenCount > 0 && (
          <Button
            kind="minimal"
            className="pl-0"
            aria-expanded={expanded}
            onClick={() => setExpanded(value => !value)}
          >
            {expanded ? t('view_less') : expanderLabel}
            <i
              className={`fa fa-chevron-down ml-xs ${css({
                transform: expanded ? 'rotate(180deg)' : 'none',
              })}`}
              aria-hidden="true"
            />
          </Button>
        )}
      </>
    )
  }

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={handleClose}
      ariaLabel={t('title')}
      hideCancel
      showBottomProceed={false}
    >
      <StatusPill tone="attention">{t('badge')}</StatusPill>
      <Text.Header3 tag="div" color="grey90" className="mt-s">
        {t('title')}
      </Text.Header3>
      <Text.BodyText4 tag="div" color="grey70" className="mt-xs">
        {t('description')}
      </Text.BodyText4>

      {notifyError && (
        <div className={css({ marginTop: '16px' })}>
          <Notification kind="negative" size="full">
            {t('notify_error')}
          </Notification>
        </div>
      )}

      <div className="mt-l">
        <Form>
          <Input
            name="message"
            componentClass="textarea"
            label={t('message_label')}
            validations={{ maxLength: MESSAGE_MAX_LENGTH }}
            inputProps={{ value: message, rows: MESSAGE_ROWS }}
            onChange={event => setDraft(event.currentTarget.value)}
          />
        </Form>
        {/* The link itself, never inside the editable draft above — an edit that
            clears the textarea must not take the link with it. */}
        <Text.BodyText4 tag="div" color="grey70" className="mt-xxs">
          {activationUrl}
        </Text.BodyText4>
      </div>

      <div className={css({ marginTop: SECTION_BREAK })}>
        <Text.BodyText5 tag="div" bold>
          {merchantName
            ? t('admins_header', { merchant: merchantName })
            : t('admins_header_fallback')}
        </Text.BodyText5>
        {renderAdmins()}
      </div>

      <div className="mt-xl d-flex justify-content-between align-items-center">
        <Button kind="secondary" onClick={onCopyLink}>
          <i className="fa fa-link mr-xs" aria-hidden="true" />
          {t('copy_link')}
        </Button>
        <Button kind="primary" onClick={handleClose}>
          {t('done')}
        </Button>
      </div>
    </ModalContainer>
  )
}

export default PreparerHandoffModal
